import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiOg } from './client';
const PATH = '/api/v1/admin/user-deletion-failures';
const ROOT_KEY = ['og', 'user-deletion-failures'];
const LIST_KEY = [...ROOT_KEY, 'list'];
const COUNT_KEY = [...ROOT_KEY, 'open-count'];
const detailKey = (failureId) => [...ROOT_KEY, 'detail', failureId.toLowerCase()];
export function buildUserDeletionFailureSearch(filters) {
    const params = new URLSearchParams();
    for (const key of ['status', 'participant', 'kind']) {
        const value = filters[key]?.trim();
        if (value)
            params.set(key, value);
    }
    params.set('limit', String(filters.limit));
    params.set('offset', String(filters.offset));
    return params.toString();
}
export function useUserDeletionFailures(filters) {
    const search = buildUserDeletionFailureSearch(filters);
    return useQuery({
        queryKey: [...LIST_KEY, filters],
        queryFn: () => apiOg('users', `${PATH}?${search}`),
        placeholderData: keepPreviousData,
    });
}
export function useUserDeletionFailure(failureId, options = {}) {
    const { enabled = true, pollUntil = 0 } = options;
    return useQuery({
        queryKey: detailKey(failureId),
        queryFn: () => apiOg('users', `${PATH}/${encodeURIComponent(failureId)}`),
        enabled: enabled && failureId !== '',
        refetchInterval: (query) => pollUntil > Date.now() && query.state.data?.status === 'open' ? 15_000 : false,
        refetchIntervalInBackground: false,
        retry: false,
    });
}
export function useOpenUserDeletionFailureCount(enabled = true) {
    return useQuery({
        queryKey: COUNT_KEY,
        queryFn: () => apiOg('users', `${PATH}?status=open&limit=1&offset=0`),
        enabled,
        select: (page) => page.total,
        refetchInterval: 180_000,
        refetchIntervalInBackground: false,
        retry: false,
    });
}
function refreshCollections(queryClient) {
    void queryClient.invalidateQueries({ queryKey: LIST_KEY });
    void queryClient.invalidateQueries({ queryKey: COUNT_KEY });
}
function refreshAfterError(queryClient, failureId) {
    refreshCollections(queryClient);
    void queryClient.invalidateQueries({ queryKey: detailKey(failureId) });
}
export function useRetryUserDeletionFailure() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (failureId) => apiOg('users', `${PATH}/${encodeURIComponent(failureId)}/retry`, { method: 'POST' }),
        onSuccess: (detail, failureId) => {
            queryClient.setQueryData(detailKey(failureId), detail);
            queryClient.setQueryData(detailKey(detail.id), detail);
            refreshCollections(queryClient);
        },
        onError: (_error, failureId) => refreshAfterError(queryClient, failureId),
    });
}
export function useResolveUserDeletionFailure() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ failureId, note }) => apiOg('users', `${PATH}/${encodeURIComponent(failureId)}/resolve`, { method: 'POST', body: { note: note.trim() } }),
        onSuccess: (detail, variables) => {
            queryClient.setQueryData(detailKey(variables.failureId), detail);
            queryClient.setQueryData(detailKey(detail.id), detail);
            refreshCollections(queryClient);
        },
        onError: (_error, variables) => refreshAfterError(queryClient, variables.failureId),
    });
}
